import styled from '@emotion/styled';
import React from 'react';
import { ApartmentIcon, HouseIcon } from 'src/components/icons';
import { baseWidths, colors } from 'src/theme';
import { Flex } from 'src/components/grid';
import { Text } from 'src/components/typography';
import { Button } from 'src/components/buttons';
import { Accordion } from 'src/components/other';
import { Card } from 'src/components/cards';
import { i18n } from 'src/common';
import { GetSoQuery } from 'src/common/api/apollo/types/types';

type SoData = NonNullable<GetSoQuery['getSo']>;

const isString = (x: unknown): x is string => typeof x === 'string';
const hasValue = (v: unknown) => v !== undefined && v !== null && v !== '';

const HeaderDataStyle = styled(Flex)({
  background: colors.background7,
  padding: '10px',
  // width: '100%',
  // ['@media (min-width: 484px)']: { width: baseWidths.mobile },
});

const AccordionWrapper = styled(Flex)({
    width: '100%',
  ['@media (min-width: 484px)']: { width: baseWidths.mobile },
});

interface DetailRow {
  id?: string;
  label: string | React.ReactNode;
  value: string | React.ReactNode;
  disabled: boolean;
}

const propertyTypeLabel = (type?: string | null) =>
  type === 'house'
    ? i18n.t('layout.headerData.propertyDetails.typeHouse')
    : i18n.t('layout.headerData.propertyDetails.typeApartment');

const usageLabel = (usage?: string | null) => {
  if (usage === 'rent') return i18n.t('layout.headerData.propertyDetails.usageRent');
  if (usage === 'permanent') return i18n.t('layout.headerData.propertyDetails.usagePermanent');
  if (usage === 'temporary') return i18n.t('layout.headerData.propertyDetails.usageTemporary');
  return '-';
};

interface Props {
  so: SoData;
  expanded: boolean | null;
  handleStyleExpansion: (value: boolean | null) => void;
}

export const HeaderDataPropertyDetails: React.FC<Props> = ({ so, expanded, handleStyleExpansion }) => {
  const pts = so.productTypeSpecific?.propertyInfo;
  const insuranceNeeds = so.productTypeSpecific?.insuranceNeeds;

  const address = [pts?.street, pts?.houseNumber].filter(Boolean).join(' ');
  const cityLine = [pts?.zipCode, pts?.city].filter(Boolean).join(' ');

  const insuredParts = [
    insuranceNeeds?.building ? i18n.t('layout.headerData.buildingPart') : null,
    insuranceNeeds?.personalProperty ? i18n.t('layout.headerData.householdPart') : null,
  ].filter(Boolean).join(' + ') || '-';

  const constructionYear = pts?.constructionYear?.before1964
    ? i18n.t('layout.headerData.propertyDetails.before1964')
    : pts?.constructionYear?.exactYear
      ? String(pts.constructionYear.exactYear)
      : null;

  const details: DetailRow[] = [
    {
      id: 'propertyType',
      label: i18n.t('layout.headerData.propertyDetails.type'),
      value: propertyTypeLabel(pts?.type),
      disabled: !pts?.type,
    },
    {
      id: 'address',
      label: i18n.t('layout.headerData.propertyDetails.address'),
      value: (
        <Flex vertical align="flex-end">
          {address && <Text type="small">{address}</Text>}
          {cityLine && <Text type="small">{cityLine}</Text>}
        </Flex>
      ),
      disabled: !address && !cityLine,
    },
    {
      id: 'area',
      label: i18n.t('layout.headerData.propertyDetails.area'),
      value: pts?.area ? `${pts.area} m²` : '-',
      disabled: !pts?.area,
    },
    {
      id: 'masonry',
      label: i18n.t('layout.headerData.propertyDetails.construction'),
      value: pts?.masonry
        ? i18n.t('layout.headerData.propertyDetails.masonry')
        : i18n.t('layout.headerData.propertyDetails.mixed'),
      disabled: !hasValue(pts?.masonry),
    },
    {
      id: 'constructionYear',
      label: i18n.t('layout.headerData.propertyDetails.constructionYear'),
      value: constructionYear ?? '-',
      disabled: !constructionYear,
    },
    {
      id: 'insured',
      label: i18n.t('layout.headerData.propertyDetails.insured'),
      value: insuredParts,
      disabled: !insuranceNeeds?.building && !insuranceNeeds?.personalProperty,
    },
    {
      id: 'usage',
      label: i18n.t('layout.headerData.propertyDetails.usage'),
      value: usageLabel(pts?.usage),
      disabled: !pts?.usage,
    },
  ];

  const PropertyIcon = pts?.type === 'house' ? HouseIcon : ApartmentIcon;

  return (
    <HeaderDataStyle vertical align="center">
      <AccordionWrapper justify='stretch' gap={16} align="flex-start">
        <Accordion
          title={(
            <Flex justify="space-between">
              <Flex gap={16} align="center" justify="space-between">
                <PropertyIcon color={colors.primary} />
                <Flex vertical align="flex-start">
                  <Text type="medium" bold>{i18n.t('layout.headerData.propertyDetails.title')}</Text>
                  <Text type="small">{propertyTypeLabel(pts?.type)}{cityLine ? ` · ${address}, ${cityLine}` : ''}</Text>
                  {pts?.area && (
                    <Text color={colors.text3} type="tiny">{`${pts.area} m²`}</Text>
                  )}
                </Flex>
              </Flex>
              <Button
                onClick={() => handleStyleExpansion(!expanded)}
                text={expanded ? i18n.t('layout.headerData.showLess') : i18n.t('layout.headerData.details')}
                size='small'
                btnType='textPrimary'
              />
            </Flex>
          )}
          content={(
            <Flex vertical gap={2}>
              {details.filter((d) => !d.disabled).map((detail) => (
                <Card
                  key={detail.id}
                  style={{
                    padding: '8px 8px',
                    backgroundColor: colors.background6,
                    borderRadius: '4px 4px 0 0',
                  }}
                >
                  <Flex gap={8} justify="space-between" align="center">
                    {isString(detail.label) ? (
                      <Text type="small">{detail.label}</Text>
                    ) : (
                      detail.label
                    )}
                    <Flex gap={8} align="flex-end" justify="flex-end" wrap="wrap">
                      {isString(detail.value) ? (
                        <Text type="small">{detail.value}</Text>
                      ) : (
                        detail.value
                      )}
                    </Flex>
                  </Flex>
                </Card>
              ))}
            </Flex>
          )}
          gap={12}
          onChange={() => handleStyleExpansion(!expanded)}
          expandIconComponent={<></>}
        />
        {/* {admin && (
          <Flex align="center" justify="center" gap={4} style={{ paddingTop: 8, minWidth: 72 }}>
            {admin.avatar ? (
              <AvatarImg src={admin.avatar} alt={admin.name} />
            ) : (
              <AvatarInitials align="center" justify="center">
                <Text type="small" bold color={colors.primary}>{initials(admin.name)}</Text>
              </AvatarInitials>
            )}
            <Flex vertical>
              <Text type="tiny" bold style={{ textAlign: 'center' }}>{admin.name}</Text>
              <Button
                btnType="textPrimary"
                size="small"
                onClick={() => { window.location.href = `tel:${admin.phone}`; }}
              >
                {i18n.t('layout.headerData.propertyDetails.callAgent')}
              </Button>
            </Flex>
          </Flex>
        )} */}
      </AccordionWrapper>
    </HeaderDataStyle>
  );
};

